import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import { Document, Page } from 'react-pdf'
import clsx from 'clsx'

import lang from '@/lang'

import { OcrImageViewer } from '@/modules/base/components/ocr/index'
import { useSignatureContext } from '@/modules/signature/_helpers/SignatureContext'
import SignaturePositionOverlay from '@/modules/signature/components/overlay/signature-position-overlay'

const documentOptions = {
  cMapUrl: 'assets/cmaps/',
  cMapPacked: true,
}

const useStyles = makeStyles(() => ({
  pdfDocument: ({ ocrModeEnabled }: { ocrModeEnabled: boolean }) => ({
    display: ocrModeEnabled ? 'none' : 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  ocrContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  image: {
    display: 'flex',
    objectFit: 'contain',
    height: '100%',
    paddingBottom: 15,
    overflowX: 'visible',
    overflowY: 'visible',
  },
  pdfPage: {
    marginLeft: 'auto',
    marginRight: 'auto',
    letterSpacing: 0,
  },
}))

interface ReceiptPreviewerItemPdfInterface {
  receiptUrl: string
  scale: number
  width: number
  rotate: number
  ocrModeEnabled: boolean
  withSignatures: boolean
  onOcrResult: (ocrResult: any) => void
  onPageLoadSuccess: () => void
}

const ReceiptPreviewerItemPdf = React.memo(function ReceiptPreviewerItemPdf(props: ReceiptPreviewerItemPdfInterface) {
  const {
    receiptUrl: url,
    scale = 1.0,
    width = null,
    rotate = 0,
    ocrModeEnabled,
    withSignatures,
    onOcrResult,
    onPageLoadSuccess,
  } = props

  const classes = useStyles(props)
  const [numPages, setNumPages] = useState(null)
  const [images, setImages] = useState([])

  let currentPage = 1
  let setContextNumPages = null
  let setPageSize = null
  if (withSignatures) {
    const { pager: { currentPage: currentPagerPage, setNumPages: setContextPagerNumPages }, setPageSize: setContextPageSize } = useSignatureContext()

    currentPage = currentPagerPage
    setContextNumPages = setContextPagerNumPages
    setPageSize = setContextPageSize
  }

  const file = useMemo(
    () => {
      if (!url) { return null }
      return url.indexOf('http') === 0 && url.indexOf('https') !== 0 ? null : { url }
    },
    [url],
  )

  useEffect(() => {
    if (!ocrModeEnabled) return

    const images = []
    const canvases = document.querySelectorAll('.pdf-page canvas')
    canvases.length && canvases.forEach((canvas: HTMLCanvasElement) => {
      images.push({
        url: canvas.toDataURL(),
        height: parseInt(canvas.style.height),
        width: parseInt(canvas.style.width),
      })
    })
    setImages(images)
  }, [ocrModeEnabled])

  const handleSetPageSize = (size) => {
    setPageSize && setPageSize(size)
  }

  const handlePageRenderSuccess = (page) => {
    if (withSignatures) {
      handleSetPageSize({ width: page.width, height: page.height })
    }
  }

  const handleDocumentLoadSuccess = useCallback(async(pdf) => {
    const { numPages } = pdf

    setNumPages(numPages)

    if (withSignatures) {
      setContextNumPages(numPages)
    }
  }, [])

  const signaturePage = useMemo(() => {
    if (!numPages) return null

    return (
      <SignaturePositionOverlay>
        <Page
          onRenderSuccess={handlePageRenderSuccess}
          key={`pdf-page-${currentPage}`}
          pageNumber={currentPage}
          scale={scale}
          width={width}
          className={clsx(classes.pdfPage, 'pdf-page')}
        />
      </SignaturePositionOverlay>
    )
  }, [numPages, currentPage, scale, width])

  const pages = useMemo(() => {
    if (!numPages) return null

    const pages = []
    for (let i = 1; i <= numPages; i++) {
      pages.push(
        <Page
          key={`pdf-page-${i}`}
          pageNumber={i}
          scale={scale}
          width={width}
          className={clsx(classes.pdfPage, 'pdf-page')}
          onLoadSuccess={onPageLoadSuccess}
        />,
      )
    }
    return pages
  }, [numPages, scale, width, onPageLoadSuccess])

  const ocrComponent = ocrModeEnabled
    ? (
      <div className={classes.ocrContainer}>
        {images.map((img, index) => {
          const { url, width, height } = img
          return (
            <OcrImageViewer
              key={index}
              src={url}
              imageStyle={{
                width,
                minWidth: width,
                height,
              }}
              customClasses={{ image: classes.image }}
              onOcrResult={onOcrResult} />
          )
        })}
      </div>
    )
    : null

  return file
    ? (
      <>
        <Document
          file={file.url}
          loading={lang.pdfLoading}
          renderMode="canvas"
          rotate={rotate}
          className={classes.pdfDocument}
          options={documentOptions}
          onLoadSuccess={handleDocumentLoadSuccess}
          error={lang.receipt.preview.errors.default}
        >
          {withSignatures ? signaturePage : pages }
        </Document>
        {ocrComponent}
      </>
    )
    : null
})

export default ReceiptPreviewerItemPdf
